"use client";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/project_components/general/popover";
import { ChevronDownIcon } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import DOMPurify from "isomorphic-dompurify";

interface LegalData {
  id: string;
  html: string;
  date: string;
}

interface LegalViewerProps {
  initialData: LegalData[];
}

export default function LegalViewer({ initialData }: LegalViewerProps) {
  const [currentId, setCurrentId] = useState(initialData[0]?.id || "");
  const [nodeOpen, setNodeOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentItem = initialData.find((item) => item.id === currentId) || initialData[0];

  useEffect(() => {
    if (contentRef.current && currentItem) {
      const sanitizedHtml = DOMPurify.sanitize(currentItem.html);
      contentRef.current.innerHTML = sanitizedHtml;
    }
  }, [currentId, currentItem?.id, currentItem?.html]);

  function handleClick(item: LegalData) {
    setCurrentId(item.id);
    setNodeOpen(false);
  }

  return (
    <>
      <div className="flex items-end w-full justify-end mt-10">
        <Popover modal={true} open={nodeOpen} onOpenChange={setNodeOpen}>
          <PopoverTrigger asChild>
            <button className="flex flex-row items-center ring-1 gap-x-2 rounded-lg px-2 py-1 ring-slate-700 text-sm font-medium justify-between cursor-pointer text-black min-w-32">
              <p>버전: {currentItem?.date}</p>
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            sideOffset={5}
            className="flex max-h-80 w-52 flex-col overflow-hidden overflow-y-auto rounded border p-1 shadow-xl bg-white text-black"
            align="start"
          >
            {initialData.map((item) => (
              <button
                key={item.id}
                onClick={() => handleClick(item)}
                onKeyDown={(e) => e.key === "Enter" && handleClick(item)}
                type="button"
                className={`flex w-full cursor-pointer items-center justify-between px-2 py-1 text-sm text-black hover:bg-gray-100 rounded ${
                  item.id === currentId ? "bg-gray-200 font-semibold" : ""
                }`}
              >
                <div className="flex flex-row items-center">
                  <div className="rounded-sm px-2">{item.date}</div>
                </div>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      </div>
      <div ref={contentRef} className="prose max-w-none" />
    </>
  );
}
